<script setup lang="ts">
import type { MarkdownElementProps } from "./MarkdownElementProps";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import { computed } from "vue";
import HorizontalSpacing from "@/views/blocks/shared/section/layout/spacing/HorizontalSpacing.vue";
import markdownit from "markdown-it";
import { handleDelegatedHashNavigation } from "@/shared/utils/router/hashNavigation";
import ScrollAnimation from "@/views/blocks/shared/section/animations/ScrollAnimation.vue";

const props = defineProps<{
    block: PageBlock<MarkdownElementProps>;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

const md = new markdownit();

const renderedMarkdown = computed(() => {
    return md.render(dataValues.value.markdown.data.text || "");
});

const proseStyle = computed(() => {
    const textColor = styleValues.value.text;
    const subtleColor = `color-mix(in srgb, ${textColor}, transparent 90%)`;
    const markerColor = `color-mix(in srgb, ${textColor}, transparent 60%)`;

    return {
        color: textColor,
        "--tw-prose-body": textColor,
        "--tw-prose-headings": textColor,
        "--tw-prose-lead": textColor,
        "--tw-prose-bold": textColor,
        "--tw-prose-captions": textColor,
        "--tw-prose-quotes": textColor,
        "--tw-prose-code": textColor,
        "--tw-prose-links": textColor,
        "--tw-prose-hr": subtleColor,
        "--tw-prose-quote-borders": subtleColor,
        "--tw-prose-th-borders": subtleColor,
        "--tw-prose-td-borders": subtleColor,
        "--tw-prose-bullets": markerColor,
        "--tw-prose-counters": markerColor,
    };
});
</script>

<template>
    <ScrollAnimation :type="scrollAnimationType">
        <div
            :id="block.webId"
            :style="{
                backgroundColor: styleValues.background,
            }"
        >
            <HorizontalSpacing
                :class="[
                    dataValues.formatting.data.paddingTop,
                    dataValues.formatting.data.paddingBottom,
                ]"
            >
                <div class="mx-auto max-w-2xl lg:max-w-7xl">
                    <EditableElement
                        :blockId="block.id"
                        field-name="markdown"
                        v-if="!dataValues.markdown.isHidden"
                    >
                        <article
                            @click="handleDelegatedHashNavigation"
                            v-html="renderedMarkdown"
                            :class="[
                                'prose prose-img:rounded-lg',
                                dataValues.formatting.data.elementAlign,
                                dataValues.formatting.data.maxWidth,
                                dataValues.formatting.data.textAlign,
                                dataValues.formatting.data.textSize,
                            ]"
                            :style="proseStyle"
                        />
                    </EditableElement>
                </div>
            </HorizontalSpacing>
        </div>
    </ScrollAnimation>
</template>
